import BusSearch from "../../components/real-time/BusSearch";
import {useCallback, useEffect, useState} from "react";
import {getLastThreePointsFromEachUnit, getUnits} from "../../lib/services/ApiCalls";
import {
    getBlockingPerUnit,
    getLastDataFromEachPoint,
    getNearestStation,
    getOrientationOfEach
} from "../../shared/translateStationIntoPositions";
import Layout from "../../layouts";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import {CircularProgress, Stack, Tab, Tabs} from "@mui/material";
import TabPanel from "../../components/TabPanel";
import dynamic from "next/dynamic";
import {
    diffWithChecked,
    filterDatasetWithUnitNumbers,
    getComingAndGoingDataset,
    wrapperForComingAndGoingDataset
} from "../../shared/utils";
import {stations} from "../../shared/stations";
import {OffServiceBuses} from "../../components/real-time/OffServiceBuses";
import {format} from "date-fns";
import Typography from "@mui/material/Typography";
import BlockedAlerts from "../../components/real-time/BlockedAlerts";
import Loading from "../../components/Loading";

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

/**
 * ChartJS instance to create the plot
 * @type {React.ComponentType<{}>}
 */
const RealTimeChart = dynamic(() => import('../../components/real-time/RealTimeChart'), {
    ssr: false,
})

/**
 * Map from leaflet
 * @type {React.ComponentType<{}>}
 */
const MapContainerWithNoSSR = dynamic(
    () => import("../../components/MapRealTime"),
    {
        ssr: false,
        loading: () => (
            <Box sx={{display: 'flex'}}>
                <CircularProgress/>
            </Box>
        )
    })

const Map = dynamic(
    () => import("../../components/real-time/Map"),
    {
        ssr: false,
        loading: () => (
            <Box sx={{display: 'flex'}}>
                <CircularProgress/>
            </Box>
        )
    })

/**
 *
 * @returns {JSX.Element}
 * @constructor
 */
const TiempoReal = () => {

    const [units, setUnits] = useState([]);
    // Saving here unit ids
    const [checked, setChecked] = useState([]);

    /**
     * Dedicated variables to calculate routes
     */
    const [orientation, setOrientation] = useState([]);
    const [nearestStations, setNearestStations] = useState([]);
    const [points, setPoints] = useState([]);

    // Two sets of variables to retrieve the coming/going units
    const [comingDataset, setComingDataset] = useState([]);
    const [goingDataset, setGoingDataset] = useState([]);
    const [saved, setSaved] = useState([]);
    const [blocking, setBlocking] = useState([]);

    /**
     * Keep tracking of the time
     */
    const [date, setDate] = useState(null);

    // Control Tabs
    const [tab, setTab] = useState(0);
    // Control loader
    const [loading, setLoading] = useState(false);

    const MINUTE_MS = 180000;

    useEffect(() => {

        // First Retrieve units
        const retrieveUnits = async () => {
            const units = await getUnits();
            let active_units = units.filter(unit => unit["is_active"] === 1);
            active_units = active_units.sort((current, next) => current.number - next.number);
            setUnits(active_units);
            setChecked(active_units.map(u => u.number));
        }

        // Retrieve Points
        const retrievePoints = async () => {
            setLoading(true);
            const lastPoints = await getLastThreePointsFromEachUnit();

            // Map Stuff
            const lastData = getLastDataFromEachPoint(lastPoints);
            const orientation = getOrientationOfEach(lastPoints);
            const nearestStation = getNearestStation(lastPoints);
            setPoints(lastData);
            setOrientation(orientation);
            setNearestStations(nearestStation);

            // Stylized stuff
            const {coming, going, saved} = getComingAndGoingDataset(lastData, orientation, nearestStation);
            setComingDataset(coming);
            setGoingDataset(going);
            setSaved(saved);

            const blocking = getBlockingPerUnit(lastPoints);
            setBlocking(blocking);
            setDate(new Date());
            setLoading(false);
        }

        const interval = setInterval(() => {
            retrievePoints();
        }, MINUTE_MS);

        if (units.length === 0 && points.length === 0) {
            retrieveUnits();
            retrievePoints();
        }

        return () => clearInterval(interval); // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
    }, [])

    return (
        <>
            <Layout>
                <Loading open={loading}></Loading>
                <Grid container spacing={2}>
                    <Grid item xs={9}>
                        {/*<Paper sx={{display: "flex", p: 0, mt: 1, border: "1px solid #b0bec5"}}>*/}
                        {/*</Paper>*/}
                        <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                            <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)}
                                  aria-label="basic tabs example">
                                <Tab label="Estilizada" {...a11yProps(0)} />
                                <Tab label="Geográfica" {...a11yProps(1)} />
                            </Tabs>
                        </Box>
                        <TabPanel value={tab} index={0}>
                            <Box sx={{p: 2, mt: 1}}>
                                <Stack>
                                    {(comingDataset.length + goingDataset.length) > 0 && (
                                        <RealTimeChart labels={stations}
                                                       comingDataset={
                                                           wrapperForComingAndGoingDataset(
                                                               comingDataset,
                                                               checked
                                                           )
                                                       }
                                                       goingDataset={
                                                           wrapperForComingAndGoingDataset(
                                                               goingDataset,
                                                               checked
                                                           )
                                                       }
                                                       blocking={blocking}/>
                                    )}
                                    <OffServiceBuses busesOff={saved}/>
                                </Stack>
                            </Box>
                        </TabPanel>
                        <TabPanel value={tab} index={1}>
                            <Box sx={{p: 2, height: "1200px"}}>
                                {/*<MapContainerWithNoSSR points={points}/>*/}
                                <Map singlePointOfUnits={
                                    filterDatasetWithUnitNumbers(
                                        [...points],
                                        diffWithChecked(
                                            [
                                                ...comingDataset.map(([meter, nearest_station, orientation, id, {data}]) => parseInt(data["unit_number"])),
                                                ...goingDataset.map(([meter, nearest_station, orientation, id, {data}]) => parseInt(data["unit_number"]))
                                            ],
                                            checked
                                        )

                                    )}
                                     offServicePoints={
                                         filterDatasetWithUnitNumbers(
                                             [...points],
                                             saved.map(([meters, id, {data}], i) => parseInt(data["unit_number"]))
                                         )
                                     }
                                />
                            </Box>
                        </TabPanel>
                    </Grid>
                    <Grid item xs={3}>
                        <Stack>
                            <Paper sx={{pt: 1, mt: 1, pb: 0, border: "1px solid #b0bec5"}}>
                                {units && units.length > 0 &&
                                    <BusSearch units={units} checked={checked} setChecked={setChecked}
                                               saved={saved}></BusSearch>}
                            </Paper>
                            <Paper sx={{pt: 1, mt: 1, pb: 0, border: "1px solid #b0bec5"}}>
                                {blocking && blocking.length > 0 && (
                                    <BlockedAlerts blocked={blocking}></BlockedAlerts>
                                )}
                            </Paper>
                            <Typography variant={"overline"} sx={{textAlign: "center"}}>
                                Última actualización: &nbsp;
                                {date !== null && (
                                    <>
                                        {format(date, 'yyyy/MM/dd HH:mm:ss')}
                                    </>
                                )}
                            </Typography>
                        </Stack>
                    </Grid>
                </Grid>
            </Layout>
        </>
    )
}

export default TiempoReal